import React, { useEffect, useMemo, useState } from "react";
import { useInstallationForm } from "../../../providers/InstallationFormProvider";
import { SlideshowLightbox } from "lightbox.js-react";
import { INSTALLATION_JOB_STORAGE_KEY } from "../InstallationJob";
import { filterOptions } from "./MachineData";

interface StoredDataType {
  machineSetup?: any[];
  currentMachineIndex?: number;
  returningFromTicket?: boolean;
  createTicket?: any;
  locationInformation?: any;
  locationContacts?: any;
  locationNotes?: any;
  locationSelection?: {
    name: string;
    address?: string;
  };
}

interface InstallationJobPreviewProps {
  installationJobDB: any;
}

const keyOr = <T,>(value: T | undefined, fallback: T): T =>
  value === undefined || value === null ? fallback : value;

const InstallationJobPreview: React.FC<InstallationJobPreviewProps> = ({
  installationJobDB,
}) => {
  const {
    headerDB = [],
    flavors = [],
    materialDB = [],
    contractOwnerDB = [],
    categoriesDB = [],
    machineServicesDB = [],
    issueRepotedDB = [],
    fetchVideos = [],
    videoOptions = [],
    blendersDB,
    tubeShieldsDB,
    cupHolderDB,
    boardsDB,
    ledRingsDB,
  } = useInstallationForm();
  const [data, setData] = useState<StoredDataType>({});

  useEffect(() => {
    try {
      const raw = localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}";
      const stored = JSON.parse(raw);
      setData(stored);
    } catch (err) {
      console.error("Failed to parse installationJob from localStorage", err);
      setData({});
    }
  }, []);

  // --- Header label ---
  const headerLabel = useMemo(() => {
    const id = installationJobDB?.headerType;
    const found = headerDB.find((h: any) => h.id === id);
    return found?.name || id || "N/A";
  }, [installationJobDB?.headerType, headerDB]);
  const ContractOwnerName = useMemo(() => {
    const id = installationJobDB?.contractOwner;
    const found = contractOwnerDB.find((h: any) => h.id === id);
    return found?.name || id || "N/A";
  }, [installationJobDB?.contractOwner, contractOwnerDB]);

  const VideoUUid = useMemo(() => {
    const id = installationJobDB?.videoUuid;
    if (!id) return "N/A";

    const found = videoOptions.find((v: any) => v.value === id);
    return found?.label || id;
  }, [installationJobDB?.videoUuid, videoOptions]);

  // --- Marketing materials ---
  const selectedMarketingMaterialNames = useMemo(() => {
    const selectedIds: string[] = keyOr<string[]>(
      installationJobDB.marketingMaterials,
      []
    );
    return selectedIds
      .map(
        (id) => materialDB.find((m: any) => String(m.id) === String(id))?.name
      )
      .filter(Boolean) as string[];
  }, [installationJobDB.marketingMaterials, materialDB]);

  // --- Machines ---
  const machineRows = useMemo(
    () => keyOr<any[]>(installationJobDB?.machines, []),
    [installationJobDB?.machines]
  );

  const recommendedServiceNamesMap = machineRows.map((machine: any) => {
    return machine?.ticket?.recommandedServices?.map((serviceId: any) => {
      const service = machineServicesDB.find((s: any) => s.id === serviceId);
      return service?.name || "N/A";
    });
  });

  const issueReportedNamesMap = machineRows.map((machine: any) => {
    return machine?.ticket?.issueReported?.map((issueId: any) => {
      const issue = issueRepotedDB.find((i: any) => i.id === issueId);
      return issue?.name || "N/A";
    });
  });
  const getFilterLabel = (value: string) => {
    const filterType = filterOptions.find((opt: any) => opt.value === value);
    return filterType ? filterType.label : "N/A";
  };

  // --- Utility to render media ---
  const renderMedia = (files: any, label?: string) => {
    if (!files) return <div className="text-muted">Not uploaded</div>;

    const arr = Array.isArray(files) ? files : [files];

    return (
      <div className="d-flex flex-wrap gap-3 mb-3">
        {arr.map((file: any, idx: number) => {
          if (!file) return null;

          if (
            file.type?.startsWith("image/") ||
            (file.url && /\.(jpg|jpeg|png|webp)$/i.test(file.url)) ||
            (file.content && /^data:image\/[a-z]+;base64,/.test(file.content))
          ) {
            return (
              <img
                key={idx}
                src={file.url || file.content}
                alt={file.name || label || `img_${idx}`}
                className="img-fluid "
              />
            );
          } else if (
            file.type?.startsWith("video/") ||
            (file.url && /\.mp4$/i.test(file.url)) ||
            (file.content && /^data:video\/[a-z]+;base64,/.test(file.content))
          ) {
            return (
              <video
                key={idx}
                src={file.url || file.content}
                controls
                className="img-fluid"
              />
            );
          }
          return null;
        })}
      </div>
    );
  };

  // --- Flavor Prices ---
  const flavorPrices: any[] = keyOr<any[]>(installationJobDB?.flavorPrice, []);

  // --- Location flags ---
  const locationFlagsSource = installationJobDB || {};

  // --- Render boolean switch ---
  const renderBoolean = (val: any) => (
    <div className="form-check form-switch form-check-custom">
      <input
        className="form-check-input"
        type="checkbox"
        checked={!!val}
        disabled
        readOnly
      />
    </div>
  );

  return (
    <div className="container py-4">
      {/* --- Location Card --- */}
      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          <h5 className="fw-bold mb-3">Location Selection</h5>
          <div className="d-flex gap-2">
            <label className="wo-label fs-4 mb-0 fw-semibold">
              Chosen Location:
            </label>
            <span className="mb-0 fs-5 fw-bold">
              {installationJobDB.locationName || "N/A"}
            </span>
          </div>
          <div className="d-flex gap-2 mt-2">
            <label className="wo-label fs-4 mb-0 fw-semibold">Address:</label>
            <span className="mb-0 fs-5 fw-bold">
              {installationJobDB.locationAddress || "N/A"}
            </span>
          </div>
        </div>
      </div>

      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          <h5 className="fw-bold mb-3">Third Party Information</h5>
          <div className="d-flex gap-2">
            <p className="mb-1"><strong>Name:</strong> {installationJobDB.thirdPartyDetail && installationJobDB.thirdPartyDetail.name ? installationJobDB.thirdPartyDetail.name : "N/A"}</p>
          </div>
          <div className="d-flex gap-2 mt-2">
            <p className="mb-1"><strong>Email:</strong> {installationJobDB.thirdPartyDetail && installationJobDB.thirdPartyDetail.email ? installationJobDB.thirdPartyDetail.email : "N/A"}</p>
          </div>
        </div>
      </div>

      {/* --- Machines --- */}
      {machineRows.length === 0 ? (
        <div className="text-muted mb-4">No machines configured.</div>
      ) : (
        machineRows.map((machine: any, idx: number) => {
          const issueReportedNames = issueReportedNamesMap[idx] || [];
          const recommendedServiceNames = recommendedServiceNamesMap[idx] || [];
          return (
            <div
              key={idx}
              className="card mx-auto mb-4"
              style={{ maxWidth: 900 }}
            >
              <div className="card-body border-dotted border-2 border-gray-200">
                <h5 className="fw-bold mb-3">
                  Machine {idx + 1}: {machine?.newMachineName || "N/A"}
                </h5>

                <div className="row">
                  <p className="mb-1">
                    <strong>Serial Number:</strong>{" "}
                    {machine?.serialNumber || "N/A"}
                  </p>
                  <p className="mb-1">
                    <strong>New Machine Name:</strong>{" "}
                    {machine?.newMachineName || "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>Deployment Date:</strong>{" "}
                    {machine?.deploymentDate
                      ? machine.deploymentDate.slice(0, 10)
                      : "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>Blender:</strong>{" "}
                    {blendersDB.find((b: any) => b.id === machine?.blenderId)
                      ?.title || "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>Tube Shield:</strong>{" "}
                    {tubeShieldsDB.find(
                      (t: any) => t.id === machine?.tubeShieldId
                    )?.name || "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>Cup Holder:</strong>{" "}
                    {cupHolderDB.find((c: any) => c.id === machine?.cupHolderId)
                      ?.title || "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>LED Ring:</strong>{" "}
                    {ledRingsDB.find((l: any) => l.id === machine?.ledRingId)
                      ?.name || "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>Board:</strong>{" "}
                    {boardsDB.find((b: any) => b.id === machine?.boardId)
                      ?.title || "N/A"}
                  </p>

                  <p className="mb-1">
                    <strong>Filter Type:</strong>{" "}
                    {getFilterLabel(machine?.filterType) || "N/A"}
                  </p>
                </div>

                {/* Boolean Checks */}
                <div className="mt-3 mb-2">
                  {[
                    { label: "Made 4 smoothies", field: "made4Smoothies" },
                    {
                      label: "Passed sanitizer test",
                      field: "passedSanitizerTest",
                    },
                    {
                      label: 'Passed "Get Status" check',
                      field: "getStatusPassed",
                    },
                    {
                      label: "Water level calibrated with scale",
                      field: "waterLevelCalibratedWithScale",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="d-flex justify-content-between align-items-center mb-2"
                    >
                      <label className="wo-label fw-semibold mb-0">
                        {item.label}
                      </label>
                      {renderBoolean(machine?.[item.field])}
                    </div>
                  ))}
                </div>

                <p className="mb-1">
                  <strong>Notes:</strong> {machine?.machineNotes || "N/A"}
                </p>
                <p className="mb-1">
                  <strong>Get status result images:</strong>
                </p>
                <div className="row g-3">
                  {(() => {
                    const source =
                      installationJobDB?.uploadImagesOrVideos
                        ?.getStatusCheckImage || [];
                    let images: any[] = [];
                    if (Array.isArray(source) && source.length > 0) {
                      if (
                        source[0] &&
                        typeof source[0] === "object" &&
                        (source[0] as any).machine_assets
                      ) {
                        const machineId =
                          machine?.id ||
                          machine?.machineId ||
                          machine?.selectedMachineId;
                        const group = (source as any[]).find(
                          (g: any) => g?.machine_id === machineId
                        );
                        images = group?.machine_assets || [];
                      } else {
                        images = (source as any[]).flat();
                      }
                    }
                    if (!images || images.length === 0) return null;
                    return images.map((item: any, index: number) => (
                      <div key={index} className="col-3">
                        <SlideshowLightbox className="container grid grid-cols-3 gap-2 mx-auto">
                          <img
                            src={item.url}
                            alt={item.name || "Media"}
                            className="img-view img-fluid position-relative overflow-hidden rounded-1 p-2 bg-light mt-2"
                            width="250"
                          />
                        </SlideshowLightbox>
                      </div>
                    ));
                  })()}
                </div>
                {/* --- Ticket --- */}

                {machine?.ticket ? (
                  <div className="mt-3 p-3 border rounded">
                    <div className="justify-content-between align-items-center mb-2 p-4">
                      <p className="mb-1">
                        <strong>Ticket ID:</strong>{" "}
                        {`SMM${machine?.ticket.ticketNumber}` || "N/A"}
                      </p>
                      <p className="mb-1">
                        <strong>Ticket Type:</strong>{" "}
                        {machine?.ticket.type || "N/A"}
                      </p>

                      <p className="mb-1">
                        <strong>Ticket Status:</strong>{" "}
                        {machine?.ticket.status || "N/A"}
                      </p>

                      <div className="gap-2 mt-2">
                        <label className="wo-label mb-0">Issue Reported:</label>
                        {issueReportedNames.length > 0 ? (
                          <ul className="mb-0">
                            {issueReportedNames.map(
                              (issueName: string, index: number) => (
                                <li key={index} className="fs-5">
                                  {issueName}
                                </li>
                              )
                            )}
                          </ul>
                        ) : (
                          <p className="text-muted">No issues reported.</p>
                        )}
                      </div>

                      <div className="gap-2 mt-2">
                        <label className="wo-label mb-0">
                          Recommended Services:
                        </label>
                        {recommendedServiceNames?.length > 0 ? (
                          <ul className="mb-0">
                            {recommendedServiceNames.map(
                              (serviceName: string, index: number) => (
                                <li key={index} className="fs-5">
                                  {serviceName}
                                </li>
                              )
                            )}
                          </ul>
                        ) : (
                          <p className="text-muted">No recommended services.</p>
                        )}
                      </div>

                      <div className="gap-2 mt-2">
                        <label className="wo-label mb-0">Other Services:</label>
                        <p className="fs-5">
                          {machine?.ticket.otherServices || "N/A"}
                        </p>
                      </div>

                      <div className="gap-2 mt-2">
                        <label className="wo-label mb-0">Comments:</label>
                        <p className="fs-5">
                          {machine?.ticket.comments || "N/A"}
                        </p>
                      </div>

                      {machine?.ticket.media?.length > 0 ? (
                        <div className="gap-2 mt-2">
                          <label className="wo-label mb-0">
                            Images & Videos:
                          </label>
                          <div className="row g-3">
                            {machine?.ticket.media.map(
                              (media: any, idx: number) => {
                                const mediaType =
                                  media.type?.toLowerCase() || "";
                                if (mediaType.includes("image") || !mediaType) {
                                  return (
                                    <div key={idx} className="col-3">
                                      <SlideshowLightbox className="container grid grid-cols-3 gap-2 mx-auto">
                                        <img
                                          src={media.url}
                                          alt={media.name || "Media"}
                                          className="img-view img-fluid position-relative overflow-hidden rounded-1 p-2 bg-light mt-2"
                                          width="250"
                                        />
                                      </SlideshowLightbox>
                                    </div>
                                  );
                                }
                                if (mediaType.includes("video")) {
                                  return (
                                    <div key={idx} className="col-6">
                                      <video
                                        src={media.url}
                                        controls
                                        className="img-view img-fluid position-relative overflow-hidden rounded-1 p-2 bg-light mt-2"
                                      />
                                    </div>
                                  );
                                }
                                return null;
                              }
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-muted">No media available</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <p className="text-muted mt-3">
                    No ticket created for this machine.
                  </p>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* --- Location Information --- */}
      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          <h4 className="mb-3">Location Information</h4>

          <div className="mb-3">
            <p className="mb-1">
              <strong>Header Type:</strong> {headerLabel || "N/A"}
            </p>
          </div>
          <div className="mb-3">
            <p className="mb-1">
              <strong>Contract Owner:</strong> {ContractOwnerName || "N/A"}
            </p>
          </div>
          <div className="mb-3">
            <p className="mb-1">
              <strong>Video for header:</strong> {VideoUUid || "N/A"}
            </p>
          </div>
          <div className="mb-3">
            <p className="mb-1">
              <strong>Last 4 digits of SIM::</strong>{" "}
              {installationJobDB?.simCardLast4digits || "N/A"}
            </p>
          </div>
          <h6 className="text-muted mt-2 mb-3">Contact Person Details</h6>

          {[
            {
              title: "Second Contact",
              name: installationJobDB?.secondContactName,
              role: installationJobDB?.secondContactRole,
              phone: installationJobDB?.secondContactPhone,
              email: installationJobDB?.secondContactEmail,
              phoneType: installationJobDB?.secondContactPhoneType,
            },
            {
              title: "Marketing Contact",
              name: installationJobDB?.marketingContactName,
              role: installationJobDB?.marketingContactRole,
              phone: installationJobDB?.marketingContactPhone,
              email: installationJobDB?.marketingContactEmail,
              phoneType: installationJobDB?.marketingContactPhoneType,
            },
            {
              title: "Store Manager Contact",
              name: installationJobDB?.storeManagerContactName,
              role: installationJobDB?.storeManagerContactRole,
              phone: installationJobDB?.storeManagerContactPhone,
              email: installationJobDB?.storeManagerContactEmail,
              phoneType: installationJobDB?.storeManagerContactPhoneType,
            },
            {
              title: "IT Contact",
              name: installationJobDB?.itContactName,
              role: installationJobDB?.itContactRole,
              phone: installationJobDB?.itContactPhone,
              email: installationJobDB?.itContactEmail,
              phoneType: installationJobDB?.itContactPhoneType,
            },
            {
              title: "Service Contact",
              name: installationJobDB?.serviceContactName,
              role: installationJobDB?.serviceContactRole,
              phone: installationJobDB?.serviceContactPhone,
              email: installationJobDB?.serviceContactEmail,
              phoneType: installationJobDB?.serviceContactPhoneType,
            },
          ].map(
            (contact, idx) =>
              (contact.name ||
                contact.role ||
                contact.phone ||
                contact.email ||
                contact.phoneType) && (
                <div
                  key={idx}
                  className="border rounded p-3 mb-3 bg-light-subtle"
                >
                  <h6 className="fw-bold mb-3">{contact.title}</h6>

                  <div className="row mb-2">
                    {contact.name && (
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Name:</strong> {contact.name || "N/A"}
                        </p>
                      </div>
                    )}
                    {contact.role && (
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Role:</strong> {contact.role || "N/A"}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="row mb-2">
                    {contact.phone && (
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Cell Phone:</strong> {contact.phone || "N/A"}
                        </p>
                      </div>
                    )}
                    {contact.email && (
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Email:</strong> {contact.email || "N/A"}
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="row mb-2">
                    {contact.phoneType && (
                      <div className="col-md-6">
                        <p className="mb-1">
                          <strong>Phone Type:</strong>{" "}
                          {contact.phoneType || "N/A"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )
          )}
        </div>
      </div>

      {/* --- Marketing Materials --- */}
      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          <h3 className="mb-2">Marketing Materials</h3>
          {selectedMarketingMaterialNames.length === 0 ? (
            <div className="text-muted">No marketing materials selected.</div>
          ) : (
            <ul className="list-unstyled ms-2">
              {selectedMarketingMaterialNames.map((name, i) => (
                <li key={i} className="mb-1 d-flex align-items-center">
                  <input
                    className="form-check-input me-2"
                    type="checkbox"
                    checked
                    disabled
                    readOnly
                  />
                  <span className="fs-6">{name}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* --- Flavor Price Table --- */}
      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          <div className="d-flex align-items-center justify-content-between">
            <h3 className="mb-0">Flavor Price</h3>
          </div>

          <table className="table table-bordered mt-3">
            <thead>
              <tr>
                <th className="fw-bold mb-0">Flavor</th>
                <th className="fw-bold mb-0">Price</th>
              </tr>
            </thead>
            <tbody>
              {flavorPrices.length === 0 ? (
                <tr>
                  <td colSpan={2} className="text-muted">
                    No flavor prices configured.
                  </td>
                </tr>
              ) : (
                flavorPrices.map((fp: any) => {
                  const flavorKey =
                    fp?.flavor ?? fp?.name ?? String(Math.random());
                  const currentFlavor = flavors?.find(
                    (f: any) => (f.flavor ?? f.name) === flavorKey
                  );
                  return (
                    <tr key={flavorKey}>
                      <td className="align-middle">
                        <input
                          className="form-check-input mb-2 me-2"
                          type="checkbox"
                          checked={fp?.is_active === true}
                          disabled
                          readOnly
                        />
                        {currentFlavor?.name || flavorKey}
                      </td>
                      <td className="align-middle">
                        <input
                          type="number"
                          className="form-control"
                          value={fp?.price ?? ""}
                          disabled
                          readOnly
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Location flags --- */}
      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          {[
            { label: "Has a smoodi router?", name: "hasSmoodiRouter" },
            { label: "Has Wifi extender?", name: "hasWifiExtender" },
            { label: "Has pressure pump?", name: "hasPressurePump" },
            { label: "Has pressure tank?", name: "hasPressureTank" },
          ].map((item, idx) => (
            <div
              key={idx}
              className="d-flex justify-content-between align-items-center mb-3"
            >
              <label className="wo-label fw-semibold mb-0">{item.label}</label>
              <div className="form-check form-switch form-check-custom">
                <input
                  type="checkbox"
                  className="form-check-input"
                  checked={!!locationFlagsSource?.[item.name]}
                  disabled
                  readOnly
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* --- Location Notes --- */}
      <div className="card mx-auto mb-4" style={{ maxWidth: 900 }}>
        <div className="card-body border-dotted border-2 border-gray-200">
          <h4 className="mb-3">Location Notes</h4>

          {
            installationJobDB?.notes && installationJobDB?.notes.length > 0 ? (
              installationJobDB?.notes.map((noteItem: any, idx: number) => (
                <div className="border border-2 border-gray-200 p-4 mb-4" key={`note-${idx}`}>
                <h6 className="fw-bold">Note: {idx+1}</h6>
                <div>
                  <span className="fs-5 fw-semibold">Category: </span>
                  <span className="fw-bold">{categoriesDB.find((cat) => cat.id == noteItem.categoryId)?.name}</span>
                </div>
                <div>
                  <span className="fs-5 fw-semibold">Note: </span>
                  <span className="fw-bold">
                    {
                      noteItem.note && noteItem.note.trim() !== ""
                        ? noteItem.note
                        : "N/A"
                    }
                  </span>
                </div>
              </div>
              ))
            ) : (
              <p className="text-muted">No notes available</p>
            )
          }
          
          {/* Required Photos */}
          {[
            { key: "imageOfFullSmoodiSetUp", label: "Full Smoodi Setup" },
            { key: "setupFreezerDoorPhoto", label: "Freezer Door Open" },
            { key: "machineBackPhoto", label: "Back of the Machine" },
            {
              key: "machineEntranceViewPhoto",
              label: "Relative to Store Entrance",
            },
          ].map((photo, idx) => (
            <div key={idx} className="mb-3">
              <label className="fw-bold d-block mb-4 fs-4">{photo.label}</label>
              {installationJobDB?.uploadImagesOrVideos?.[photo.key]?.length >
              0 ? (
                renderMedia(
                  installationJobDB?.uploadImagesOrVideos?.[photo.key],
                  photo.label
                )
              ) : (
                <p className="text-muted">No media available</p>
              )}
            </div>
          ))}

          {/* Walkthrough Video */}
          <div className="mb-4">
            <h6 className="fw-bold fs-4">Walkthrough Video</h6>
            {installationJobDB?.uploadImagesOrVideos?.storeWalkthroughVideo
              ?.length > 0 ? (
              renderMedia(
                installationJobDB?.uploadImagesOrVideos?.storeWalkthroughVideo,
                "Walkthrough Video"
              )
            ) : (
              <p className="text-muted">No media available</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallationJobPreview;
